/**
 * DESIGN TOKENS — founder Master Brief Part 5.
 * Single source of truth for grounds, ikos, kili yellow, type, spacing.
 * The shared design-system export.
 */
export const NIGHT = {
  sunken:  "#12122A",
  ground:  "#1A1A30",
  surface: "#20203A",
  raised:  "#2F2F52",
  text:    "#FFFFFF",
  muted:   "#C9C9DA",
  dim:     "#8A8AB0", // labels and eyebrows only, never body copy
};

export const CREAM = {
  ground:   "#FBF4E9",
  card:     "#FFFFFF", // #FFFFFF is a card value, never a ground
  well:     "#F1E7D6",
  hairline: "#E4D7C0",
  text:     "#1A1A30",
  muted:    "#5C5548",
};

// RETIRED — must not be re-introduced.
export const RETIRED_COLORS = ["#211D14", "#8A8171"];

// IKOs — three ramps each. Rules per brief:
//   NIGHT: bright does both jobs (fill and text).
//   CREAM: bright FILLS ONLY; deep carries the words.
//   companion = pattern artwork only.
//   text on a bright fill = night ink #1A1A30, never white.
export const IKO = {
  sherehe: { bright: "#FF6B9D", deep: "#D1257B", companion: "#8A3A5C", label: "Sherehe" },
  polite:  { bright: "#9D4EDD", deep: "#9946DC", companion: "#5B3A7A", label: "Polite" },
  // Corrected (Vania 30 Sep late) · michezo is BLUE, kazi is GREEN.
  // Contrast rules: polite text needs ≥18.66px bold and never on #2F2F52.
  // Michezo blue is fine on ground + card surface (#20203A); on raised
  // #2F2F52 it must also be ≥18.66px bold. Sherehe and kazi pass at any size.
  michezo: { bright: "#5390FF", deep: "#0E63FF", companion: "#31518C", label: "Michezo" },
  kazi:    { bright: "#44DD88", deep: "#0E8148", companion: "#2C7150", label: "Kazi" },
};
export const IKO_KEYS = ["sherehe", "polite", "michezo", "kazi"];

// Iko-key -> the appropriate "text" colour on each ground.
export function ikoText(key, ground /* "night" | "cream" */) {
  const iko = IKO[key];
  if (!iko) return ground === "night" ? NIGHT.text : CREAM.text;
  return ground === "night" ? iko.bright : iko.deep;
}
// Msg 247 · convenience for the light-mode swap. Same rule as ikoText but
// accepts the theme string used by ThemeProvider.
export function ikoTextForTheme(key, theme /* "night" | "cream" */) {
  return ikoText(key, theme === "cream" ? "cream" : "night");
}
// Iko fill colour (both grounds use bright). Text ON a bright fill is night ink.
export function ikoFill(key) {
  return IKO[key]?.bright || "#333";
}
export const ON_IKO_FILL_TEXT = "#1A1A30";

// KILI YELLOW — three permitted uses in priority order.
export const KILI = "#FFD60A";
export const KILI_USES = ["score-and-roses", "primary-cta", "integrity-callout"];
// If a page carries a score, the CTA becomes a white outline (see components/CTA).
// NAMED EXCEPTIONS that do NOT spend the page's yellow: logo centre dot, pattern artwork.

// TYPE
export const TYPE = {
  displayFamily: `"Poppins", Arial, sans-serif`,
  bodyFamily:    `Arial, "Helvetica Neue", Helvetica, sans-serif`,
  weight: { heading: 800, label: 600, ui: 500 },
  tracking: { display: "-0.05em", headline: "-0.035em", subhead: "-0.02em", body: "0", eyebrow: "0.22em" },
  size: {
    display:  { size: "132px", line: "0.86" },
    headline: { size: "76px",  line: "0.9"  },
    subhead:  { size: "34px",  line: "1.1"  },
    body:     { size: "24px",  line: "1.55" },
    label:    { size: "20px",  line: "1", tracking: "0.22em", transform: "uppercase" },
  },
};
// HEADINGS ARE ALWAYS LOWERCASE.
// EYEBROWS ARE THE ONLY UPPERCASE IN THE SYSTEM.

// Brand-case normaliser — sweep-time helper for any string that might carry
// "Watu Wako" or "WATU WAKO" as titlecase/caps. Preserves the eyebrow context.
export function toBrandCase(s, { preserveEyebrow = false } = {}) {
  if (!s) return s;
  if (preserveEyebrow && /^WATU WAKO\s*·\s*/.test(s)) return s;
  return s.replace(/watu\s*wako/gi, "watu wako");
}

// Time-of-day tokens — founder Msg 71 Part 0b. Not a nightlife app.
export const TIME_OF_DAY = [
  { key: "morning",   label: "Morning",   startHr: 5,  endHr: 11 },
  { key: "afternoon", label: "Afternoon", startHr: 12, endHr: 16 },
  { key: "evening",   label: "Evening",   startHr: 17, endHr: 21 },
  { key: "night",     label: "Late",      startHr: 22, endHr: 4  },
  { key: "all_day",   label: "All day",   startHr: null, endHr: null },
];
export function timeOfDayFor(startAtIso) {
  if (!startAtIso) return null;
  const hr = new Date(startAtIso).getHours();
  if (hr >= 5 && hr <= 11) return "morning";
  if (hr >= 12 && hr <= 16) return "afternoon";
  if (hr >= 17 && hr <= 21) return "evening";
  return "night";
}
